import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { message, Modal, Select, Table, TableProps } from "antd";
import Spinner from "../../components/shared/Spinner/Spinner";
import {
  OrderStatusType,
  ProductOrderssDataType,
} from "../../interfaces/admin.interface";
import { OrdersService } from "../../services/orders.service";
import { OrderStatusService } from "../../services/order_status.service";
import MainButton from "../../components/shared/MainButton/MainButton";
import { MdFileDownload } from "react-icons/md";
import { ExcelDowloadServices } from "../../services/downloadExcell.service";

const ProductOrders = () => {
  const [orders, setOrders] = useState<ProductOrderssDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [status, setStatus] = useState<OrderStatusType[]>([]);
  const [applyDetails, setApplyDetails] =
    useState<ProductOrderssDataType | null>(null);

  useEffect(() => {
    setLoading(true);
    OrdersService.getProductOrders()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response.data.data.map(
            (item: ProductOrderssDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setOrders(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    OrderStatusService.getAllStatus().then((response) => {
      if (Array.isArray(response?.data?.data)) {
        setStatus(response.data.data);
      }
    });
  }, []);

  const handleChangeStatus = async (orderId: number, statusId: number) => {
    try {
      const response = await OrdersService.changeProductsOrderStatus(
        orderId,
        statusId
      );
      if (response.status === 200) {
        const updatedOrders = orders.map((order) =>
          order.id === orderId
            ? {
                ...order,
                status_id: statusId,
              }
            : order
        );
        setOrders(updatedOrders);
        message.success(response.data.message);
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Xəta baş verdi");
    }
  };

  const columns: TableProps<ProductOrderssDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => Number(a.key) - Number(b.key),
    },
    {
      title: "Sifariş kodu",
      dataIndex: "order_code",
    },
    {
      title: "Müştəri",
      dataIndex: "full_name",
      sorter: (a, b) => (a.full_name || "").localeCompare(b.full_name || ""),
    },
    {
      title: "E-poçt",
      dataIndex: "email",
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
      render: (_, record) =>
        record.email ? (
          <a className="content emailContent" href={`mailto:${record.email}`}>
            {record.email}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Status",
      dataIndex: "status_id",
      render: (_, record) => (
        <Select
          value={record.status_id}
          style={{ width: "150px" }}
          options={status.map((el) => ({
            label: el.name,
            value: el.id,
          }))}
          onChange={(value) => handleChangeStatus(record.id, value)}
        />
      ),
    },
    {
      title: "Sifariş tarixi",
      dataIndex: "created_at",
      render: (_, record) => <p>{record.created_at}</p>,
    },
  ];

  // 🔹 Məhsullar üçün ayrıca cədvəl sütunları
  const productColumns: TableProps<
    ProductOrderssDataType["products"][number]
  >["columns"] = [
    {
      title: "Məhsul adı",
      dataIndex: "name",
    },
    {
      title: "Model kodu",
      dataIndex: "model_code",
    },
    {
      title: "Qiymət",
      dataIndex: "price",
      render: (_, product) => `${product.pivot?.price || product.price} AZN`,
    },
    {
      title: "Miqdar",
      dataIndex: "pivot",
      render: (_, product) => product.pivot?.quantity || "-",
    },
    {
      title: "Tracking nömrə",
      dataIndex: "tracking_number",
    },
  ];

  const handleDownload = async () => {
    try {
      const res = await ExcelDowloadServices.getProductExcell();
      const blob = new Blob([res.data], {
        type: "application/vnd.ms-excel",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "product_orders.xls");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error("Excel yüklənərkən xəta baş verdi");
    }
  };

  return (
    <div id="pages">
      <Helmet>
        <title>Orhan Door | Məhsul sifarişləri</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Məhsul sifarişləri</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleDownload}>
              <MdFileDownload size={25} />
            </MainButton>
            <div className="pages_table">
              <Table
                columns={columns}
                dataSource={orders}
                rowKey={(record) => record.id.toString()}
                pagination={false}
                expandable={{
                  expandedRowRender: (record) => (
                    <Table
                      columns={productColumns}
                      dataSource={
                        record.products?.map((p, index) => ({
                          ...p,
                          key: p.id || index,
                        })) || []
                      }
                      pagination={false}
                      size="small"
                    />
                  ),
                  rowExpandable: (record) =>
                    !!record.products && record.products.length > 0,
                }}
              />
            </div>
          </div>
        </div>
      )}
      {/* Modal hələ saxlanıb, istəsən tam silə də bilərsən */}
      <Modal
        title={"Məhsullar"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        width={"100%"}
        centered
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {applyDetails?.products?.map((el) => (
            <div key={el.id}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <img
                  style={{ width: "50%", objectFit: "cover" }}
                  src={el.cover_image}
                  alt={el.name}
                />
              </div>
              <p style={{ textAlign: "center" }}>{el.name}</p>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default ProductOrders;
